import React, { useReducer, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import ColorToolsContext from './ColorToolsContext';
import { initialColorList, initialStyleGuideOnStage } from './colorToolsIntialStates';

const initialState = {
    colorOnStage: undefined,
    setColorOnStage: () => {},
    imageOnStage: undefined,
    imageSources: [],
    setImageOnStage: () => {},
    imageColorList: initialColorList,
    setImageColorList: () => {},
    imageColorConfigurationOnStage: {
        change: 0,
    },
    setImageColorConfigurationOnStage: () => {},
    colorMixedOnStage: undefined,
    setColorMixedOnStage: () => {},
    stepsOnStage: undefined,
    wheelViewOnStage: undefined,
    setStepsOnStage: () => {},
    harmonyOnStage: undefined,
    setHarmonyOnStage: () => {},
    harmonyIntensityOnStage: undefined,
    harmonyDominanceOnStage: undefined,
    setHarmonyDominanceOnStage: () => {},
    colorListOnStage: initialColorList,
    setColorListOnStage: () => {},
    styleGuideOnStage: initialStyleGuideOnStage,
    setStyleGuideOnStage: () => {},
    userFromClient: undefined,
    setUserFromClient: () => {},
    allColorsUserFromClient: undefined,
    setAllColorsUserFromClient: () => {},
    selectedColors: undefined,
    setSelectedColors: () => {},
};

const actions = {
    SET_COLOR_ON_STAGE: 'SET_COLOR_ON_STAGE',
    SET_IMAGE_ON_STAGE: 'SET_IMAGE_ON_STAGE',
    SET_IMAGE_COLOR_LIST: 'SET_IMAGE_COLOR_LIST',
    SET_IMAGE_COLOR_CONFIGURATION_ON_STAGE: 'SET_IMAGE_COLOR_CONFIGURATION_ON_STAGE',
    SET_COLOR_MIXED_ON_STAGE: 'SET_COLOR_MIXED_ON_STAGE',
    SET_STEPS_ON_STAGE: 'SET_STEPS_ON_STAGE',
    SET_WHEEL_VIEW_ON_STAGE: 'SET_WHEEL_VIEW_ON_STAGE',
    SET_HARMONY_ON_STAGE: 'SET_HARMONY_ON_STAGE',
    SET_HARMONY_INTENSITY_ON_STAGE: 'SET_HARMONY_INTENSITY_ON_STAGE',
    SET_HARMONY_DOMINANCE_ON_STAGE: 'SET_HARMONY_DOMINANCE_ON_STAGE',
    SET_COLOR_LIST_ON_STAGE: 'SET_COLOR_LIST_ON_STAGE',
    SET_STYLE_GUIDE_ON_STAGE: 'SET_STYLE_GUIDE_ON_STAGE',
    SET_USER_FROM_CLIENT: 'SET_USER_FROM_CLIENT',
    SET_ALL_COLORS_USER_FROM_CLIENT: 'SET_ALL_COLORS_USER_FROM_CLIENT',
    SET_SELECTED_COLORS: 'SET_SELECTED_COLORS',
};

const reducer = (state, action) => {
    switch (action.type) {
        case actions.SET_COLOR_ON_STAGE:
            return {
                ...state,
                colorOnStage: action.colorValue,
            };
        case actions.SET_IMAGE_ON_STAGE:
            return {
                ...state,
                imageOnStage: action.imageValue,
                imageSources: action.imageSources || [],
            };
        case actions.SET_IMAGE_COLOR_LIST:
            return {
                ...state,
                imageColorList: action.imageColorListValue,
            };
        case actions.SET_IMAGE_COLOR_CONFIGURATION_ON_STAGE:
            return {
                ...state,
                imageColorConfigurationOnStage: action.imageColorConfigurationValue,
            };
        case actions.SET_COLOR_MIXED_ON_STAGE:
            return {
                ...state,
                colorMixedOnStage: action.colorMixedValue,
            };
        case actions.SET_STEPS_ON_STAGE:
            return {
                ...state,
                stepsOnStage: action.steps,
            };
        case actions.SET_WHEEL_VIEW_ON_STAGE:
            if (state.wheelViewOnStage === action.wheelView) return state;
            return { ...state, wheelViewOnStage: action.wheelView };
        case actions.SET_HARMONY_ON_STAGE:
            return {
                ...state,
                harmonyOnStage: action.harmony,
            };
        case actions.SET_HARMONY_INTENSITY_ON_STAGE:
            if (state.harmonyIntensityOnStage === action.harmonyIntensity) return state;
            return { ...state, harmonyIntensityOnStage: action.harmonyIntensity };
        case actions.SET_HARMONY_DOMINANCE_ON_STAGE:
            return {
                ...state,
                harmonyDominanceOnStage: action.harmonyDominance,
            };
        case actions.SET_COLOR_LIST_ON_STAGE:
            return {
                ...state,
                colorListOnStage: action.colorListOnStage,
            };
        case actions.SET_STYLE_GUIDE_ON_STAGE:
            return {
                ...state,
                styleGuideOnStage: action.styleGuideOnStage || initialStyleGuideOnStage,
            };
        case actions.SET_USER_FROM_CLIENT:
            return { ...state, userFromClient: action.userFromClient };
        case actions.SET_ALL_COLORS_USER_FROM_CLIENT:
            return { ...state, allColorsUserFromClient: action.allColorsUserFromClient };
        case actions.SET_SELECTED_COLORS:
            return {
                ...state,
                selectedColors: action.selectedColors,
            };
        default:
            return state;
    }
};

const ColorToolsProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState);

    const setHarmonyIntensityOnStage = useCallback((harmonyIntensity) => {
        dispatch({ type: actions.SET_HARMONY_INTENSITY_ON_STAGE, harmonyIntensity });
    }, []);
    const setWheelViewOnStage = useCallback((wheelView) => {
        dispatch({ type: actions.SET_WHEEL_VIEW_ON_STAGE, wheelView });
    }, []);
    const setColorListOnStage = useCallback((colorListOnStage) => {
        dispatch({ type: actions.SET_COLOR_LIST_ON_STAGE, colorListOnStage });
    }, []);

    const value = useMemo(
        () => ({
            colorOnStage: state.colorOnStage,
            setColorOnStage: (colorValue) => {
                dispatch({ type: actions.SET_COLOR_ON_STAGE, colorValue });
            },
            imageOnStage: state.imageOnStage,
            imageSources: state.imageSources,
            setImageOnStage: (imageValue, imageSources) => {
                dispatch({ type: actions.SET_IMAGE_ON_STAGE, imageValue, imageSources });
            },
            imageColorList: state.imageColorList,
            setImageColorList: (imageColorListValue) => {
                dispatch({ type: actions.SET_IMAGE_COLOR_LIST, imageColorListValue });
            },
            imageColorConfigurationOnStage: state.imageColorConfigurationOnStage,
            setImageColorConfigurationOnStage: (imageColorConfigurationValue) => {
                dispatch({
                    type: actions.SET_IMAGE_COLOR_CONFIGURATION_ON_STAGE,
                    imageColorConfigurationValue,
                });
            },
            colorMixedOnStage: state.colorMixedOnStage,
            setColorMixedOnStage: (colorMixedValue) => {
                dispatch({ type: actions.SET_COLOR_MIXED_ON_STAGE, colorMixedValue });
            },
            stepsOnStage: state.stepsOnStage,
            wheelViewOnStage: state.wheelViewOnStage,
            setWheelViewOnStage,
            setStepsOnStage: (steps) => {
                dispatch({ type: actions.SET_STEPS_ON_STAGE, steps });
            },
            harmonyOnStage: state.harmonyOnStage,
            setHarmonyOnStage: (harmony) => {
                dispatch({ type: actions.SET_HARMONY_ON_STAGE, harmony });
            },
            harmonyDominanceOnStage: state.harmonyDominanceOnStage,
            setHarmonyDominanceOnStage: (harmonyDominance) => {
                dispatch({ type: actions.SET_HARMONY_DOMINANCE_ON_STAGE, harmonyDominance });
            },
            harmonyIntensityOnStage: state.harmonyIntensityOnStage,
            setHarmonyIntensityOnStage,
            colorListOnStage: state.colorListOnStage,
            setColorListOnStage,
            styleGuideOnStage: state.styleGuideOnStage,
            setStyleGuideOnStage: (styleGuideOnStage) => {
                dispatch({ type: actions.SET_STYLE_GUIDE_ON_STAGE, styleGuideOnStage });
            },
            userFromClient: state.userFromClient,
            setUserFromClient: (userFromClient) => {
                dispatch({
                    type: actions.SET_USER_FROM_CLIENT,
                    userFromClient,
                });
            },
            allColorsUserFromClient: state.allColorsUserFromClient,
            setAllColorsUserFromClient: (allColorsUserFromClient) => {
                dispatch({
                    type: actions.SET_ALL_COLORS_USER_FROM_CLIENT,
                    allColorsUserFromClient,
                });
            },
            selectedColors: state.selectedColors,
            setSelectedColors: (selectedColors) => {
                dispatch({
                    type: actions.SET_SELECTED_COLORS,
                    selectedColors,
                });
            },
        }),
        [state, setHarmonyIntensityOnStage, setColorListOnStage, setWheelViewOnStage],
    );

    return <ColorToolsContext.Provider value={value}>{children}</ColorToolsContext.Provider>;
};

ColorToolsProvider.propTypes = {
    children: PropTypes.node,
};

ColorToolsProvider.defaultProps = {
    children: undefined,
};

export default ColorToolsProvider;
